import { useState, useEffect } from 'react';
import { getCompanies, getNominalCodes, createNominalCode, updateNominalCode, deleteNominalCode } from '../services/api';
import { Company, NominalCode, CreateNominalCodeRequest } from '../types/api';

export default function NominalCodes() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [codes, setCodes] = useState<NominalCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCode, setEditingCode] = useState<NominalCode | null>(null);
  const [formData, setFormData] = useState<CreateNominalCodeRequest>({
    company_id: 0,
    nominal_code: '',
    description: '',
    category: '',
    vat_rate: undefined,
    default_tax_code: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadCodes();
    }
  }, [selectedCompany]);

  const loadCompanies = async () => {
    try {
      const data = await getCompanies();
      setCompanies(data);
      if (data.length > 0) {
        setSelectedCompany(data[0].id);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCodes = async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const data = await getNominalCodes(selectedCompany);
      setCodes(data);
    } catch (error) {
      console.error('Failed to load nominal codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    if (!selectedCompany) return;
    setEditingCode(null);
    setFormData({
      company_id: selectedCompany,
      nominal_code: '',
      description: '',
      category: '',
      vat_rate: undefined,
      default_tax_code: ''
    });
    setShowModal(true);
  };

  const handleEdit = (code: NominalCode) => {
    setEditingCode(code);
    setFormData({
      company_id: code.company_id,
      nominal_code: code.nominal_code,
      description: code.description || '',
      category: code.category || '',
      vat_rate: code.vat_rate || undefined,
      default_tax_code: code.default_tax_code || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (code: NominalCode) => {
    if (!confirm(`Are you sure you want to deactivate nominal code "${code.nominal_code}"?`)) return;

    try {
      await deleteNominalCode(code.id);
      loadCodes();
    } catch (error) {
      console.error('Failed to delete nominal code:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingCode) {
        await updateNominalCode(editingCode.id, {
          nominal_code: formData.nominal_code,
          description: formData.description,
          category: formData.category,
          vat_rate: formData.vat_rate,
          default_tax_code: formData.default_tax_code
        });
      } else {
        await createNominalCode(formData);
      }
      setShowModal(false);
      loadCodes();
    } catch (error) {
      console.error('Failed to save nominal code:', error);
    } finally {
      setSaving(false);
    }
  };

  const categoryOptions = [
    'Sales',
    'Purchases',
    'Direct Expenses',
    'Overheads',
    'Fixed Assets',
    'Current Assets',
    'Current Liabilities',
    'Long Term Liabilities',
    'Capital & Reserves'
  ];

  const taxCodeOptions = ['T0', 'T1', 'T2', 'T5', 'T9', 'T20'];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-bold text-gray-900">Nominal Codes</h1>
          <select
            value={selectedCompany || ''}
            onChange={(e) => setSelectedCompany(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.company_name}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={handleCreate}
          disabled={!selectedCompany}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Nominal Code
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">VAT Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tax Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {codes.map((code) => (
                <tr key={code.id} className={!code.is_active ? 'bg-gray-50 opacity-60' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-mono font-medium text-gray-900">{code.nominal_code}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {code.description || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {code.category || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {code.vat_rate !== null ? `${code.vat_rate}%` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {code.default_tax_code || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      code.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {code.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button
                      onClick={() => handleEdit(code)}
                      className="text-blue-600 hover:text-blue-800 mr-3"
                    >
                      Edit
                    </button>
                    {code.is_active && (
                      <button
                        onClick={() => handleDelete(code)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {codes.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No nominal codes yet. Click "Add Nominal Code" to create one.
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {editingCode ? 'Edit Nominal Code' : 'Add Nominal Code'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nominal Code *
                </label>
                <input
                  type="text"
                  required
                  value={formData.nominal_code}
                  onChange={(e) => setFormData({ ...formData, nominal_code: e.target.value })}
                  placeholder="e.g., 4000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Sales - UK"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select category...</option>
                  {categoryOptions.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    VAT Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.vat_rate || ''}
                    onChange={(e) => setFormData({ ...formData, vat_rate: e.target.value ? Number(e.target.value) : undefined })}
                    placeholder="e.g., 20"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Tax Code
                  </label>
                  <select
                    value={formData.default_tax_code}
                    onChange={(e) => setFormData({ ...formData, default_tax_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select...</option>
                    {taxCodeOptions.map((tc) => (
                      <option key={tc} value={tc}>{tc}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingCode ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
